import { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { UserPlus, Pencil, Trash2, ShieldCheck } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const getAuthHeaders = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('admin_token')}` } });

const ROLE_BADGES = {
  super_admin: 'bg-purple-50 text-purple-700',
  admin: 'bg-blue-50 text-[#0047AB]',
};

const AdminUsersPage = () => {
  const [users, setUsers] = useState([]);
  const [actor, setActor] = useState(null); // { username, role, level }
  const [roleOptions, setRoleOptions] = useState([]); // [{value, label, level}]
  const [forbidden, setForbidden] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [form, setForm] = useState({ username: '', name: '', password: '', role: 'admin' });
  const [saving, setSaving] = useState(false);

  const fetchUsers = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/users`, getAuthHeaders());
      setUsers(res.data.users || []);
      setActor(res.data.actor || null);
      setRoleOptions(res.data.role_options || []);
      setForbidden(false);
    } catch (e) {
      if (e?.response?.status === 403) setForbidden(true);
      else toast.error('Failed to load admin users');
    }
  }, []);

  useEffect(() => { fetchUsers(); }, [fetchUsers]);

  const defaultRoleValue = useMemo(() => {
    if (!roleOptions.length) return 'admin';
    // Default to the lowest-level role available to the actor (safer default)
    return [...roleOptions].sort((a, b) => a.level - b.level)[0].value;
  }, [roleOptions]);

  const openCreate = () => {
    setEditingUser(null);
    setForm({ username: '', name: '', password: '', role: defaultRoleValue });
    setDialogOpen(true);
  };

  const openEdit = (user) => {
    setEditingUser(user);
    setForm({ username: user.username, name: user.name || '', password: '', role: user.role || 'admin' });
    setDialogOpen(true);
  };

  const canManage = (user) => {
    if (!actor) return false;
    if (user.is_primary) return false; // env-based primary cannot be edited from UI
    if ((user.level ?? 0) > actor.level) return false;
    return true;
  };

  const canDelete = (user) => {
    if (!canManage(user)) return false;
    return user.username?.toLowerCase() !== actor?.username?.toLowerCase();
  };

  const canToggleActive = (user) => canDelete(user); // same rule: manage + not self

  const handleSave = async () => {
    setSaving(true);
    try {
      if (editingUser) {
        const payload = { name: form.name, role: form.role };
        if (form.password) payload.password = form.password;
        await axios.put(`${API}/admin/users/${editingUser.id}`, payload, getAuthHeaders());
        toast.success('Admin user updated');
      } else {
        await axios.post(`${API}/admin/users`, form, getAuthHeaders());
        toast.success(`Admin user "${form.username}" created`);
      }
      setDialogOpen(false);
      fetchUsers();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (user) => {
    try {
      await axios.put(`${API}/admin/users/${user.id}`, { active: !user.active }, getAuthHeaders());
      toast.success(!user.active ? `"${user.username}" activated` : `"${user.username}" deactivated`);
      fetchUsers();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to update user');
    }
  };

  const handleDelete = async (user) => {
    if (!window.confirm(`Delete admin user "${user.username}"? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/admin/users/${user.id}`, getAuthHeaders());
      toast.success('Admin user deleted');
      fetchUsers();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to delete');
    }
  };

  if (forbidden) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center bg-white border border-slate-200 rounded-lg p-10 max-w-md" data-testid="users-forbidden">
          <ShieldCheck className="w-10 h-10 text-slate-300 mx-auto mb-3" />
          <h2 className="font-semibold text-slate-900">Restricted</h2>
          <p className="text-sm text-slate-500 mt-1">You do not have permission to view admin users.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="max-w-5xl mx-auto px-6 py-10">
        <div className="flex items-center justify-between mb-8 flex-wrap gap-3">
          <div>
            <h1 className="text-3xl font-outfit font-bold text-slate-900 mb-1">Admin Users</h1>
            <p className="text-sm text-slate-500">
              Manage teammates who can access the admin panel.
              {actor && (
                <span className="ml-2 text-slate-400">
                  You are signed in as <span className="font-medium text-slate-700">{actor.username}</span>
                  {' '}({actor.role.replace('_', ' ')})
                </span>
              )}
            </p>
          </div>
          <Button
            className="bg-[#0047AB] hover:bg-[#003380]"
            onClick={openCreate}
            data-testid="add-admin-user-btn"
            disabled={roleOptions.length === 0}
            title={roleOptions.length === 0 ? 'No roles available to assign' : undefined}
          >
            <UserPlus className="w-4 h-4 mr-2" /> Add Admin User
          </Button>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-slate-200 overflow-hidden">
          <table className="w-full">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <th className="px-6 py-3 text-left text-sm font-semibold text-slate-900">Username</th>
                <th className="px-6 py-3 text-left text-sm font-semibold text-slate-900">Name</th>
                <th className="px-6 py-3 text-left text-sm font-semibold text-slate-900">Role</th>
                <th className="px-6 py-3 text-left text-sm font-semibold text-slate-900">Status</th>
                <th className="px-6 py-3 text-left text-sm font-semibold text-slate-900">Created</th>
                <th className="px-6 py-3 text-right text-sm font-semibold text-slate-900">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200">
              {users.map((user) => {
                const isSelf = user.username?.toLowerCase() === actor?.username?.toLowerCase();
                return (
                  <tr key={user.id} data-testid={`admin-user-row-${user.username}`}>
                    <td className="px-6 py-4 text-sm font-mono font-semibold text-slate-900">
                      {user.username}
                      {isSelf && (
                        <span className="ml-2 text-[10px] uppercase tracking-wider text-slate-400">you</span>
                      )}
                      {user.is_primary && (
                        <span className="ml-2 inline-flex items-center gap-1 text-[10px] font-bold uppercase px-1.5 py-0.5 rounded bg-blue-50 text-[#0047AB]">
                          <ShieldCheck className="w-3 h-3" /> Primary
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4 text-sm text-slate-600">{user.name || '—'}</td>
                    <td className="px-6 py-4">
                      <span
                        className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${ROLE_BADGES[user.role] || 'bg-slate-100 text-slate-600'}`}
                        data-testid={`role-badge-${user.username}`}
                      >
                        {user.role === 'super_admin' && <ShieldCheck className="w-3 h-3" />}
                        {(user.role || 'admin').replace('_', ' ')}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${user.active ? 'bg-green-100 text-green-800' : 'bg-slate-100 text-slate-500'}`}>
                        {user.active ? 'Active' : 'Deactivated'}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-xs text-slate-500">
                      {user.created_at ? new Date(user.created_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: '2-digit' }) : '—'}
                    </td>
                    <td className="px-6 py-4 text-right">
                      <div className="flex gap-1 justify-end">
                        {canToggleActive(user) && (
                          <Button variant="ghost" size="sm" onClick={() => toggleActive(user)} data-testid={`toggle-user-${user.username}`}>
                            {user.active ? 'Deactivate' : 'Activate'}
                          </Button>
                        )}
                        {canManage(user) && (
                          <Button variant="ghost" size="icon" onClick={() => openEdit(user)} title="Edit" data-testid={`edit-user-${user.username}`}>
                            <Pencil className="w-4 h-4" />
                          </Button>
                        )}
                        {canDelete(user) && (
                          <Button variant="ghost" size="icon" className="text-red-500 hover:text-red-700" onClick={() => handleDelete(user)} title="Delete" data-testid={`delete-user-${user.username}`}>
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{editingUser ? `Edit "${editingUser.username}"` : 'Add Admin User'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {!editingUser && (
              <div>
                <Label htmlFor="au-username">Username *</Label>
                <Input
                  id="au-username"
                  value={form.username}
                  onChange={(e) => setForm({ ...form, username: e.target.value })}
                  placeholder="e.g. rahul"
                  className="mt-1"
                  data-testid="admin-user-username-input"
                />
              </div>
            )}
            <div>
              <Label htmlFor="au-name">Full Name</Label>
              <Input
                id="au-name"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                className="mt-1"
                data-testid="admin-user-name-input"
              />
            </div>
            <div>
              <Label htmlFor="au-role">Role *</Label>
              <select
                id="au-role"
                value={form.role}
                onChange={(e) => setForm({ ...form, role: e.target.value })}
                className="mt-1 block w-full h-10 rounded-md border border-slate-200 bg-white px-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#0047AB]/40"
                data-testid="admin-user-role-select"
                disabled={editingUser && editingUser.username?.toLowerCase() === actor?.username?.toLowerCase()}
              >
                {roleOptions.map((r) => (
                  <option key={r.value} value={r.value}>
                    {r.label} — level {r.level}
                  </option>
                ))}
              </select>
              {editingUser && editingUser.username?.toLowerCase() === actor?.username?.toLowerCase() && (
                <p className="text-xs text-slate-400 mt-1">You cannot change your own role.</p>
              )}
            </div>
            <div>
              <Label htmlFor="au-password">{editingUser ? 'New Password (leave blank to keep current)' : 'Password *'}</Label>
              <Input
                id="au-password"
                type="password"
                value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
                placeholder="Min 6 characters"
                className="mt-1"
                data-testid="admin-user-password-input"
              />
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
              <Button
                className="bg-[#0047AB] hover:bg-[#003380]"
                disabled={saving || (!editingUser && (!form.username || form.password.length < 6))}
                onClick={handleSave}
                data-testid="admin-user-save-btn"
              >
                {saving ? 'Saving…' : editingUser ? 'Update User' : 'Create User'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminUsersPage;
